import React, { useRef, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import useSupercluster from 'use-supercluster';
import styles from './AlertsMap.module.css';
import config from 'config/config';
import { GeoLocationPoint, PointProperties, Location } from 'model';
import { useFitBounds } from './effects/useFitBounds';
import { openInfoWindow } from './infoWindow';
import { Marker } from './Marker';
import { useTranslation } from 'react-i18next';
import { getDistance } from "../../helper/maps";


type AlertsMapProps = {
  geoLocationPoints: GeoLocationPoint<Location>[];
}


export const AlertsMap: React.FC<AlertsMapProps> = React.memo(({geoLocationPoints}) => {
  const mapRef = useRef<google.maps.Map>();
  const [bounds, setBounds] = useState<number[] | null>(null);
  const [zoom, setZoom] = useState(10);

  const { t } = useTranslation();
  useFitBounds(geoLocationPoints, mapRef.current);

  const { clusters, supercluster } = useSupercluster({
    points: geoLocationPoints,
    bounds,
    zoom,
    options: {
      radius: 75,
      maxZoom: 22,
      map: (props: PointProperties<Location>) => ({
        pendingStats: { ...props.pendingStats },
        coordinates: { ...props.coordinates },
        distance: props.distance,
        city: props.city,
        state: props.state,
        locations: [ ...props.locations ],
      }),
      reduce: (acc: PointProperties<Location>, props: PointProperties<Location>) => {
        acc.pendingStats = {
          warningCount: acc.pendingStats.warningCount + props.pendingStats.warningCount,
          criticalCount: acc.pendingStats.criticalCount + props.pendingStats.criticalCount,
          infoCount: acc.pendingStats.infoCount + props.pendingStats.infoCount,
        }

        acc.distance += getDistance(acc.coordinates, props.coordinates);
        acc.locations = [
          ...acc.locations,
          ...props.locations
        ];
        return acc;
      },
    }
  });

  const unitedStatesCenter = {
    lat: 39.157302,
    lng: -99.116640,
  };

  const handleClusterOnClick = (cluster: GeoLocationPoint<Location>) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    if (cluster.properties.distance > 20) {
      const expansionZoom = Math.min(
        supercluster.getClusterExpansionZoom(cluster.id),
        20
      );
      mapRef.current && mapRef.current.setZoom(expansionZoom);
    } else {
      mapRef.current && openInfoWindow(cluster, mapRef.current);
    }

    mapRef.current && mapRef.current.panTo({ lat: latitude, lng: longitude });
  };

  const handleLocationOnClick = (cluster: GeoLocationPoint<Location>) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    if (mapRef.current) {
      openInfoWindow(cluster, mapRef.current);
      mapRef.current.panTo({ lat: latitude, lng: longitude });
    }
  };

  return (
    <div
      className = {styles.root}
    >
      <div className={styles.header}>
        <span>{`${t('Issues')} *`}</span>
        { geoLocationPoints.length < 1 && <span className={styles.noAlerts}>({t('no-alerts-message')})</span> }
      </div>
      <div className={styles.mapContainer}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: config.googleMapsKey as string, libraries: 'geometry'}}
          defaultCenter={unitedStatesCenter}
          options={{
            fullscreenControl: false,
            maxZoom: 20
          }}
          defaultZoom={4}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map }) => {
            mapRef.current = map;
          }}
          onChange={({ zoom, bounds }) => {
            setZoom(zoom);
            setBounds([
              bounds.nw.lng,
              bounds.se.lat,
              bounds.se.lng,
              bounds.nw.lat
            ]);
          }}
        >
          {clusters.map((cluster: GeoLocationPoint<Location>) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const { cluster: isCluster } = cluster.properties;
            const markerId = isCluster ? `cluster:${cluster.id}` : `loc:${cluster.properties.locationId}`;

            return (
              <Marker
                key           = {markerId}
                markerId      = {markerId}
                lat           = {latitude}
                lng           = {longitude}
                cluster       = {cluster}
                totalPoints   = {geoLocationPoints.length}
                onClick       = {(isCluster, cluster) => {
                  if (isCluster) {
                    handleClusterOnClick(cluster);
                  } else {
                    handleLocationOnClick(cluster);
                  }
                }}
              />
            );
          })}
        </GoogleMapReact>
      </div>
    </div>
  );
});
